import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import moment from "moment";
import ListHead from "../table/ListHead";
import Scrollbar from "../table/Scrollbar";
import SearchNotFound from "../table/SearchNotFound";
import MoreMenu from "./MoreMenu";
import {
  getAccountFilterConditions,
  Log,
  sendDataWithPost,
} from "../../services/services";
import EditDialogs from "./Dialogs/EditDialogs";
import DeleteDialog from "./Dialogs/DeleteDialog";
import { StyledTableRow } from "../TableRow";
import FormSearch from "../FormSearch";
import CircularLoader from "../circularLoader";
import { pageNumber, rowsPerPage } from "../../utils/constants";
import Pagination from "../Pagination";
import { applySortFilter } from "../../utils/tableFunctions";
import Label from "../Label";
import AccountDetails from "../../pages/details/admin/AccountDetails";
import HeaderHelpPage from "../HeaderHelpPage";
import CommonDialog from "../CustomDialog";

const defaultValue = {
  is_active: true
}

const TABLE_HEAD = [
  // { id: "id", label: "ID", align: "center" },
  { id: "full_name", label: "Name" },
  { id: "email", label: "Email" },
  { id: "group", label: "Role" },
  { id: "oxygen7_entity", label: "Entity", align: "center" },
  { id: "date_joined", label: "Active Date", align: "center" },
  { id: "last_login", label: "Last Login", align: "center" },
  { id: "is_active", label: "Active" },
  { id: "action" },
];

const ViewAccount = ({ userRole }) => {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [loaded, setLoaded] = useState(false);

  const [pagination, setPagination] = useState({
    page: pageNumber,
    page_size: rowsPerPage,
  });
  const [tableData, setTableData] = useState({ results: [] });
  const [filter, setFilter] = useState(defaultValue);
  const [inputValues, setInputValues] = useState({});

  // dialogs
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedId, setSelectedId] = useState();
  const [itemId, setItemId] = useState(null);

  //filter
  const [accountFilter, setAccountFilter] = useState("");
  const [fLoaded, setFLoaded] = useState(false);

  const CustomTableCell = ({ align, field, id }) => {
    return (
      <TableCell
        align={align}
        onClick={() => {
          // window.open(`/3/app/account-details/${id}/`, "_blank");
          setItemId(id);
        }}
      >
        {field}
      </TableCell>
    );
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const queryPage = (queryParams, params) => {
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `/admins/account/filter/?${queryString}`;
    loaded && setLoaded(false);
    sendDataWithPost(url, params || filter, renderData, setLoaded, () => {
      setPagination(queryParams);
    });
  };

  const handleChangePage = (event, newPage) => {
    queryPage({
      page: newPage + 1,
      page_size: pagination.page_size,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    queryPage({
      page: 1,
      page_size: event.target.value,
    })
  };

  const filteredList = applySortFilter(
    tableData.results || [],
    order,
    orderBy
  );

  const isItemNotFound = tableData.results.length === 0;

  const renderData = res => {
    const newData = res.results.map(item => ({
      ...item,
      group: item.group?.name,
      oxygen7_entity: item.oxygen7_entity?.entity
    }))

    setTableData({
      ...res,
      results: newData
    });
  }

  useEffect(() => {
    queryPage(pagination);
    getAccountFilterConditions(setAccountFilter, setFLoaded);
  }, []);

  const handleCloseDialog = () => {
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
  };

  const onFilterSubmit = (fParams) => {
    const newFParams = { ...fParams, email: inputValues?.email?.trim() || fParams.email?.trim() };
    const newPagination = {
      ...pagination,
      page: 1,
    };

    if (newFParams.is_active === 'all') delete newFParams.is_active;
    Log.info('Click button [Apply Filter] - Search Account list', newFParams);

    setFilter(newFParams);
    queryPage(newPagination, newFParams);
  };

  const schema = [
    {
      type: "search-select",
      label: "Email",
      param: "email",
      options: accountFilter?.email?.map(email => email),
      inputValues,
      setInputValues,
    },
    {
      type: "select",
      label: "Group",
      param: "group",
      id: false,
      options: accountFilter?.group?.map((grp) => grp)
    },
    {
      type: "select",
      label: "Active",
      param: "is_active",
      options: [
        { name: "All", id: "all" },
        { name: "Yes", id: true },
        { name: "No", id: false },
      ]
    }
  ]

  return (
    <Box>
      <FormSearch
        schema={schema}
        loading={!fLoaded}
        onSearch={onFilterSubmit}
        defaultValue={defaultValue}
      />
      {loaded ?
        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  onRequestSort={handleRequestSort}
                  checkbox={false}
                />
                <TableBody>
                  {loaded &&
                    filteredList.map((row) => {
                        const { id, full_name, email, is_active, group, oxygen7_entity, date_joined, last_login } = row;

                        return (
                          <StyledTableRow
                            hover
                            key={id}
                            sx={{ cursor: "pointer" }}
                          >
                            {/* <CustomTableCell
                              align="center"
                              field={index + 1}
                              id={id}
                            /> */}
                            <CustomTableCell
                              field={full_name}
                              id={id}
                            />
                            <CustomTableCell
                              field={email}
                              id={id}
                            />
                            <CustomTableCell
                              field={group ?? "N/A"}
                              id={id}
                            />
                            <CustomTableCell
                              align="center"
                              field={oxygen7_entity}
                              id={id}
                            />
                            <CustomTableCell
                              align="center"
                              field={date_joined && moment(date_joined).format("DD/MM/YYYY")}
                              id={id}
                            />
                            <CustomTableCell
                              align="center"
                              field={last_login && moment(last_login).format("DD/MM/YYYY")}
                              id={id}
                            />
                            <CustomTableCell
                              field={
                                <Label color={is_active ? "success" : "error"}>
                                  {is_active ? "YES" : "NO"}
                                </Label>
                              }
                              id={id}
                            />

                            <TableCell align="right">
                              {is_active && (userRole.includes("Edit") ||
                                userRole.includes("Delete")) && (
                                <MoreMenu
                                  setOpenDeleteDialog={setOpenDeleteDialog}
                                  setOpenEditDialog={setOpenEditDialog}
                                  setRow={() => setSelectedId(id)}
                                  userRole={userRole}
                                  is_active={is_active}
                                />
                              )}
                            </TableCell>
                          </StyledTableRow>
                        );
                      })}
                </TableBody>
                {isItemNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <SearchNotFound />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          {!isItemNotFound && (
            <Pagination
              count={tableData.count}
              pagination={pagination}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Card>
        :
        <CircularLoader />
      }
      {selectedId && (
        <>
          {openEditDialog && (
            <EditDialogs
              open={true}
              onClose={handleCloseDialog}
              selectedId={selectedId}
              setResponse={renderData}
              setTableLoaded={setLoaded}
            />
          )}
          {openDeleteDialog && (
            <DeleteDialog
              open={true}
              onClose={handleCloseDialog}
              selectedId={selectedId}
              setResponse={renderData}
              setLoaded={setLoaded}
            />
          )}
        </>
      )}

      {itemId && (
        <CommonDialog
          title={<HeaderHelpPage title="Account Details" variant="h5" mb={0} />}
          onClose={() => setItemId(null)}
        >
          <AccountDetails itemId={itemId} />
        </CommonDialog>
      )}
    </Box>
  );
};

export default ViewAccount;
